"use client";

import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Image from "@/components/ui/image";
import { useNewsletterSubscription } from "@/hooks/use-newsletter-subscription";
import { hasAuthSession } from "@/lib/supabase-lazy";
import { cn } from "@/lib/utils";
import type { NewsletterIssue, NewsletterShowcase } from "@/lib/newsletter/showcase";

/**
 * The pieces every App Store Times signup surface shares (the modal, the home card,
 * the /designs card), so the pitch, the form and the live data never drift apart.
 */

/** One query for every surface; a stuck request gives up instead of spinning. */
export const showcaseQueryOptions = {
	queryKey: ["newsletter-showcase"],
	staleTime: 60 * 60 * 1000,
	retry: 1,
	queryFn: async ({ signal }: { signal: AbortSignal }) => {
		// AbortSignal.any is Safari 17.4+; older browsers just get no timeout.
		const timed = typeof AbortSignal.any === "function" ? AbortSignal.any([signal, AbortSignal.timeout(8000)]) : signal;
		const res = await fetch("/api/newsletter/showcase", { signal: timed });
		if (!res.ok) throw new Error(`newsletter showcase: ${res.status}`);
		return (await res.json()) as NewsletterShowcase;
	},
};

export const useNewsletterShowcase = (enabled = true) => useQuery({ ...showcaseQueryOptions, enabled });

export function NewsletterBrandRow({ readers, className }: { readers: number | null | undefined; className?: string }) {
	return (
		<div className={cn("flex h-8 items-center gap-2.5", className)}>
			<Image src="/brand/ast-icon-square.png" alt="" width={64} height={64} className="h-8 w-8 rounded-[9px]" />
			<span className="text-sm font-semibold text-[var(--ast-brand-text)]">App Store Times</span>
			{readers ? (
				<span className="rounded-full border border-[rgb(var(--ast-brand-moss-rgb))] px-2 py-0.5 text-xs text-[rgb(var(--ast-brand-text-rgb)/0.7)]">
					<span className="font-semibold tabular-nums text-[var(--ast-brand-text)]">{readers.toLocaleString("en-US")}</span> readers
				</span>
			) : null}
		</div>
	);
}

/**
 * Email field and button. A signed-out reader is handed to the login dialog to
 * confirm the address (subscribeToNewsletter does that); `onSignInHandoff` lets a
 * modal get out of its way first.
 */
export function NewsletterSignupForm({
	medium,
	defaultEmail = "",
	stacked = false,
	onSignInHandoff,
	inputId = "newsletter-email",
}: {
	medium: string;
	defaultEmail?: string;
	/** Field above button at every width, for narrow columns. */
	stacked?: boolean;
	onSignInHandoff?: () => void;
	inputId?: string;
}) {
	const [email, setEmail] = useState(defaultEmail);
	const { error, isLoading, isSuccess, reset, subscribe } = useNewsletterSubscription(medium);
	// Read after mount: the session lives in a cookie the server render does not see.
	const [signedOut, setSignedOut] = useState(false);
	useEffect(() => setSignedOut(!hasAuthSession()), []);
	const errorId = `${inputId}-error`;
	const row = !stacked;

	const handleSubmit = async (event: React.FormEvent) => {
		event.preventDefault();
		if (!email.trim()) return;
		if (!hasAuthSession()) onSignInHandoff?.();
		if (await subscribe(email, medium)) setEmail("");
	};

	return (
		<div role="status">
			{isSuccess ? (
				<div className="flex items-start gap-3 rounded-xl border border-[rgb(var(--ast-brand-forest-rgb)/0.55)] bg-[rgb(var(--ast-brand-green-rgb)/0.08)] px-4 py-4">
					<CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[var(--ast-brand-green)]" />
					<div>
						<div className="text-base font-semibold text-[var(--ast-brand-text)]">You&apos;re subscribed.</div>
						<p className="mt-1 text-sm leading-6 text-[rgb(var(--ast-brand-text-rgb)/0.65)]">The next issue will be in your inbox this week.</p>
					</div>
				</div>
			) : (
				<form
					onSubmit={handleSubmit}
					className={cn(
						"flex flex-col gap-2",
						row &&
							"sm:h-14 sm:flex-row sm:items-center sm:gap-1.5 sm:rounded-xl sm:border sm:border-[rgb(var(--ast-brand-moss-rgb))] sm:bg-[rgb(var(--ast-brand-night-rgb)/0.7)] sm:p-1.5 sm:focus-within:border-[rgb(var(--ast-brand-green-rgb)/0.7)] sm:focus-within:ring-4 sm:focus-within:ring-[rgb(var(--ast-brand-green-rgb)/0.12)]",
					)}
				>
					<Input
						id={inputId}
						type="email"
						aria-label="Email address"
						placeholder="you@company.com"
						autoComplete="email"
						value={email}
						onChange={(event) => {
							if (error) reset();
							setEmail(event.target.value);
						}}
						className={cn(
							"h-12 min-w-0 flex-1 rounded-xl border-[rgb(var(--ast-brand-moss-rgb))] bg-[rgb(var(--ast-brand-night-rgb)/0.7)] text-base text-[var(--ast-brand-text)] placeholder:text-[rgb(var(--ast-brand-text-rgb)/0.4)] focus-visible:ring-[rgb(var(--ast-brand-green-rgb)/0.5)]",
							row && "sm:h-full sm:rounded-lg sm:border-0 sm:bg-transparent sm:focus-visible:ring-0",
						)}
						disabled={isLoading}
						aria-invalid={!!error}
						aria-describedby={error ? errorId : undefined}
					/>
					<Button
						type="submit"
						className={cn(
							"h-12 shrink-0 rounded-xl bg-[var(--ast-brand-green)] px-5 text-base font-semibold text-[var(--ast-brand-night)] hover:bg-[#52ff8d]",
							row && "sm:h-full sm:rounded-lg",
						)}
						disabled={isLoading || !email.trim()}
					>
						{isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Get the next issue"}
					</Button>
				</form>
			)}
			{error && (
				<p id={errorId} role="alert" className="mt-2 text-sm text-red-300">
					{error}
				</p>
			)}
			{!isSuccess && (
				<p className="mt-3 text-xs leading-5 text-[rgb(var(--ast-brand-text-rgb)/0.65)]">
					Free. Unsubscribe in one click.{signedOut ? " We'll email you a link to confirm your address." : ""}
				</p>
			)}
		</div>
	);
}

export function RecentIssues({ issues, pending, limit = 4, className }: { issues: NewsletterIssue[]; pending: boolean; limit?: number; className?: string }) {
	if (pending) {
		return (
			<div className={cn("space-y-2", className)} aria-hidden="true">
				<div className="h-4 w-24 rounded bg-white/[0.04]" />
				{Array.from({ length: Math.min(limit, 3) }, (_, i) => <div key={i} className="h-[38px] animate-pulse rounded bg-white/[0.04]" />)}
			</div>
		);
	}
	if (issues.length === 0) return null;
	return (
		<div className={className}>
			<p className="text-xs font-medium text-[rgb(var(--ast-brand-text-rgb)/0.6)]">Recent issues</p>
			<ul className="mt-2 border-t border-[rgb(var(--ast-brand-moss-rgb)/0.6)]">
				{issues.slice(0, limit).map((issue) => (
					<li key={issue.url} className="border-b border-[rgb(var(--ast-brand-moss-rgb)/0.6)]">
						<a
							href={issue.url}
							target="_blank"
							rel="noopener noreferrer"
							className="group grid grid-cols-[4.25rem_minmax(0,1fr)_auto] items-baseline gap-3 py-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ast-brand-green-rgb)/0.5)]"
						>
							<span className="text-xs font-semibold text-[var(--ast-brand-green)]">{issue.kind === "releases" ? "Releases" : "Design"}</span>
							<span className="truncate font-medium text-[var(--ast-brand-text)] group-hover:underline group-hover:underline-offset-4">{issue.hook}</span>
							<ArrowUpRight className="h-3.5 w-3.5 self-center text-[var(--ast-brand-text)] opacity-40 transition-opacity group-hover:opacity-100" aria-hidden="true" />
							<span className="sr-only">(opens the issue in a new tab)</span>
						</a>
					</li>
				))}
			</ul>
		</div>
	);
}
